import { useTranslations } from '@vocab/react';
import { Button, Column, Columns, Inline } from 'braid-design-system';
import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import {
  JOB_CHECKOUT_PRESSED,
  JOB_PUBLISH_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { PreviewButton } from 'src/app/v1/views/shared/PreviewButton';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

export const EditPageActions = (props: {
  onPublish: () => Promise<void>;
  onCancel: () => void;
  onError: (type: Error | string) => void;
  resetError: () => void;
}) => {
  const { onPublish, onCancel, onError, resetError } = props;

  const { editFlowCheckout } = useSelector(
    (state: AppState) => ({
      editFlowCheckout: state.session.editFlowCheckout,
    }),
    shallowEqual,
  );

  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const { t } = useTranslations(translations);

  useEffect(
    () => () => {
      // cleanup local state when component unmounts
      setPublishing(false);
    },
    [],
  );

  const onPublishClick = async () => {
    if (published) {
      log.error('editPage | onPublishClick | already published');

      return;
    }

    try {
      const eventData: TrackingEvent = {
        eventName: editFlowCheckout.enabled
          ? JOB_CHECKOUT_PRESSED
          : JOB_PUBLISH_PRESSED,
      };

      getTrackingAdapter.sendLinkEvent(eventData);

      resetError();

      setPublishing(true);

      await onPublish();

      if (!editFlowCheckout.enabled) {
        setPublished(true);
      }
    } catch (error: any) {
      onError(error);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Columns space="small" collapseBelow="tablet" reverse>
      <Column width="content">
        <Button
          id={editFlowCheckout.enabled ? 'next-page-button' : 'publish-button'}
          tone="brandAccent"
          onClick={onPublishClick}
          loading={publishing || editFlowCheckout.loading}
        >
          {editFlowCheckout.enabled
            ? t('edit-page-actions-button-checkout')
            : t('edit-page-actions-button-publish')}
        </Button>
      </Column>
      <Column width="content">
        <PreviewButton />
      </Column>
      <Column>
        <Inline space="none" collapseBelow="tablet">
          <Button
            variant="transparent"
            onClick={onCancel}
            data={{ testid: 'close-without-making-changes-button' }}
          >
            {t('edit-page-actions-button-cancel')}
          </Button>
        </Inline>
      </Column>
    </Columns>
  );
};
